/**
 * MODULE: domain/sales-target-management/frontend/api/sales-targets-client
 * PURPOSE: API client for Sales Target Management endpoints
 * NAMING: Sales Targets = revenue quotas (NOT platform usage quotas)
 * DEPENDENCIES: @shared/lib/frontend/api/config (apiV1Url), @shared/lib/frontend/api/errors (fetchWith429Retry, getErrorMessage)
 * AGENT SAFETY: SAFE
 * FILE SIZE: 197 / 250 lines
 * Recent: 2026-05-03 — [2.2.782] **`fetchWith429Retry`** on **`request`**; **`requestUrl`** + **`apiV1Url`**. 2026-02-24 — getErrorMessage for API error handling.
 */

import { apiV1Url, getAuthHeaders } from '@shared/lib/frontend/api/config';
import { fetchWith429Retry, getErrorMessage } from '@shared/lib/frontend/api/errors';
import type {
  SalesTarget,
  SalesTargetCreate,
  SalesTargetUpdate,
  SalesTargetPerformance,
  TeamSalesTargetRollup,
  ForecastEntry,
  ForecastEntryCreate,
  ForecastEntryUpdate,
  SalesTargetProgress,
} from '../../contracts/sales-targets.types';

const BASE_PATH = '/api/v1/sales-targets';

/** Map **`/api/v1/...`** call sites to **`apiV1Url`** (Axios or fetch never keep Compose-only hosts). */
function requestUrl(endpoint: string): string {
  const raw = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const afterV1 = raw.replace(/^\/api\/v1(?=\/)/i, '');
  return apiV1Url(afterV1.startsWith('/') ? afterV1 : `/${afterV1}`);
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = requestUrl(endpoint);
  const response = await fetchWith429Retry(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    const err = new Error(getErrorMessage(error, `HTTP ${response.status}`)) as Error & {
      status: number;
    };
    err.status = response.status;
    throw err;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const salesTargetsApi = {
  // =====================================================
  // SALES TARGETS
  // =====================================================

  listTargets: async (filters?: {
    user_id?: string;
    territory_id?: string;
    fiscal_year?: number;
    fiscal_quarter?: number;
    period_type?: string;
    status?: string;
    skip?: number;
    limit?: number;
  }): Promise<SalesTarget[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const url = params.toString() ? `${BASE_PATH}/?${params.toString()}` : `${BASE_PATH}/`;
    return request<SalesTarget[]>(url);
  },

  getTarget: async (id: string): Promise<SalesTarget> => {
    return request<SalesTarget>(`${BASE_PATH}/${id}`);
  },

  createTarget: async (data: SalesTargetCreate): Promise<SalesTarget> => {
    return request<SalesTarget>(`${BASE_PATH}/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateTarget: async (id: string, data: SalesTargetUpdate): Promise<SalesTarget> => {
    return request<SalesTarget>(`${BASE_PATH}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteTarget: async (id: string): Promise<void> => {
    return request<void>(`${BASE_PATH}/${id}`, { method: 'DELETE' });
  },

  // =====================================================
  // PROGRESS & ANALYTICS
  // =====================================================

  getProgress: async (id: string): Promise<SalesTargetProgress> => {
    return request<SalesTargetProgress>(`${BASE_PATH}/${id}/progress`);
  },

  listPerformance: async (filters?: {
    user_id?: string;
    territory_id?: string;
    fiscal_year?: number;
    health_status?: string;
    skip?: number;
    limit?: number;
  }): Promise<SalesTargetPerformance[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const url = `${BASE_PATH}/performance${params.toString() ? `?${params.toString()}` : ''}`;
    return request<SalesTargetPerformance[]>(url);
  },

  // =====================================================
  // TEAM ROLL-UP
  // =====================================================

  listTeamRollup: async (manager_id?: string): Promise<TeamSalesTargetRollup[]> => {
    const params = manager_id ? `?manager_id=${manager_id}` : '';
    return request<TeamSalesTargetRollup[]>(`${BASE_PATH}/team-rollup${params}`);
  },

  // =====================================================
  // FORECAST ENTRIES
  // =====================================================

  listForecasts: async (filters?: {
    sales_target_id?: string;
    forecast_category?: string;
    user_id?: string;
    skip?: number;
    limit?: number;
  }): Promise<ForecastEntry[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const url = `${BASE_PATH}/forecasts${params.toString() ? `?${params.toString()}` : ''}`;
    return request<ForecastEntry[]>(url);
  },

  getForecast: async (id: string): Promise<ForecastEntry> => {
    return request<ForecastEntry>(`${BASE_PATH}/forecasts/${id}`);
  },

  createForecast: async (data: ForecastEntryCreate): Promise<ForecastEntry> => {
    return request<ForecastEntry>(`${BASE_PATH}/forecasts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateForecast: async (id: string, data: ForecastEntryUpdate): Promise<ForecastEntry> => {
    return request<ForecastEntry>(`${BASE_PATH}/forecasts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteForecast: async (id: string): Promise<void> => {
    return request<void>(`${BASE_PATH}/forecasts/${id}`, { method: 'DELETE' });
  },

  // =====================================================
  // HEALTH CHECK
  // =====================================================

  healthCheck: async () => {
    return request<{ status: string; service: string; note: string }>(`${BASE_PATH}/health`);
  },

  getAccessScope: async () => {
    return request<import('../../contracts/sales-targets.types').SalesTargetAccessScope>(
      `${BASE_PATH}/access-scope`
    );
  },

  getTerritoryAlignment: async (
    territoryId: string,
    fiscalYear: number,
    fiscalQuarter: number
  ) => {
    const params = new URLSearchParams({
      fiscal_year: String(fiscalYear),
      fiscal_quarter: String(fiscalQuarter),
    });
    return request<import('../../contracts/sales-targets.types').TerritoryQuotaAlignment>(
      `${BASE_PATH}/territory-alignment/${territoryId}?${params.toString()}`
    );
  },

  applyTerritoryQuotaSuggestions: async (
    territoryId: string,
    body: {
      fiscal_year: number;
      fiscal_quarter: number;
      assignments: Array<{ user_id: string; target_amount: number }>;
    }
  ) => {
    return request<{ created_count: number; skipped_count: number }>(
      `${BASE_PATH}/territory-alignment/${territoryId}/apply`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  },
};
